import { useNavigate } from "react-router-dom";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import { useEffect, useState } from "react";
import SearchBar from "../../components/SearchBar";
import AddIcon from "@mui/icons-material/Add";
import AddShoppingCategory from "../../Modals/addshoppingcategory";
import DeleteConfirmModal from "../../Modals/deleteConfirmModal";
import {
  useLazyGetCategoryQuery,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} from "../../services/shopingMeal";
import { isValidInput } from "../../utils/validations";
import { showError, showToast } from "../../constants/toasts";
import Pagination from "../../components/Panigation";
import usePermission from "../../hooks/usePermission";

const ShoppingMeals = () => {
  const navigate = useNavigate();

  // Permission hook for Shopping Meals module
  const { 
    hasPermission, 
    isLoading: permissionLoading, 
    canView, 
    canAddEdit, 
    canDelete 
  } = usePermission({
    module: "Shopping Meals",
    permission_type: "can_view"
  });

  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleCloseModal = (): void => {
    setOpen(false);
  };
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<any[]>([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [categoryUpdate] = useUpdateCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();
  const [getCategory] = useLazyGetCategoryQuery();
  const [rows, setRows] = useState<any[]>([]);
  const totalPages = Math.ceil(totalCount / 10);
  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const getUserList = async () => {
    setIsLoading(true);
    try {
      const response = await getCategory({
        length: 10,
        start: page,
        search: debouncedSearchTerm,
      }).unwrap();
      if (response.status === 200) {
        setRows(response?.data?.response_object || []);
        setTotalCount(response?.data?.total_records || 0);
      }
      setIsLoading(false);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    getUserList();
  }, [debouncedSearchTerm, page]);
  const handleEdit = (id: number) => {
    setSelectedId(id);
    setIsEdit(true);
    setOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    try {
      await deleteCategory({ id: selectedId }).unwrap();
      showToast("Category deleted successfully");
      getUserList();
    } catch (e: any) {
      showError(e?.data?.message || "Failed to delete category");
    }
    setDeleteOpen(false);
    setSelectedId(null);
  };

  // Show loading while checking permissions
  if (permissionLoading) {
    return <div>Loading permissions...</div>;
  }

  // Show access denied if user doesn't have view permission
  if (!hasPermission) {
    return <div>Access Denied</div>;
  }

  return (
    <>
      <>
        <div className="main_title">
          <h1>Shopping & Meal AI</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> -
            Shopping & Meal AI
          </p>
        </div>

        <div className="cards">
          <div className="table_header">
            <div className="left_s">
              <Box className="search_box">
                <SearchBar
                  placeholder="Search..."
                  searchTerm={searchTerm}
                  setDebouncedSearchTerm={setDebouncedSearchTerm}
                  value={searchTerm}
                  onCross={() => setSearchTerm("")}
                  onChange={(val: any) => {
                    if (isValidInput(val.target.value)) {
                      setSearchTerm(val.target.value);
                    }
                  }}
                />
              </Box>
            </div>
            {canAddEdit("Shopping Meals") && (
              <div className="right_s">
                <Button
                  className="btn btn_primary"
                  onClick={() => {
                    setIsEdit(false);
                    setSelectedId(null);
                    setOpen(true);
                  }}
                >
                  <AddIcon /> Add{" "}
                </Button>
              </div>
            )}
          </div>
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow hover>
                  <TableCell>Food Category</TableCell>
                  <TableCell>Sub Categories</TableCell>
                  {(canAddEdit("Shopping Meals") || canDelete("Shopping Meals")) && (
                    <TableCell>Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow hover key={row.id}>
                    <TableCell>
                      <Typography fontWeight="bold">{row.name}</Typography>
                    </TableCell>

                    <TableCell>
                      <Button
                        className="btn sm btn_primary"
                        onClick={() =>
                          navigate(`/shopping-meals/sub-categories/${row.id}`)
                        }
                      >
                        Sub Categories
                      </Button>
                    </TableCell>
                    {(canAddEdit("Shopping Meals") || canDelete("Shopping Meals")) && (
                      <TableCell>
                        <div className="table_actions v2">
                          {canAddEdit("Shopping Meals") && (
                            <Box>
                              <IconButton onClick={() => handleEdit(row.id)}>
                                <img
                                  src="/static/images/edit2_icon.svg"
                                  alt="Edit"
                                />
                              </IconButton>
                            </Box>
                          )}
                          {canDelete("Shopping Meals") && (
                            <Box>
                              <IconButton
                                onClick={() => {
                                  setSelectedId(row.id);
                                  setDeleteOpen(true);
                                }}
                              >
                                <img
                                  src="/static/images/trash2_icon.svg"
                                  alt="Delete"
                                />
                              </IconButton>
                            </Box>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </>
      <Pagination
        module={rows}
        page={page}
        onPageChange={onPageChange}
        totalPages={totalPages}
        totalCount={totalCount}
        setPage={setPage}
      />
      {canAddEdit("Shopping Meals") && (
        <AddShoppingCategory
          open={open}
          onClose={handleCloseModal}
          setOpen={setOpen}
          editId={selectedId ?? undefined}
          isEdit={isEdit}
          onSuccess={getUserList}
        />
      )}
      {canDelete("Shopping Meals") && (
        <DeleteConfirmModal
          open={deleteOpen}
          setOpen={setDeleteOpen}
          handleDelete={handleDelete}
          title="Are you sure you want to delete this category?"
          message="This action cannot be undone."
        />
      )}
    </>
  );
};

export default ShoppingMeals;
